import type {
  DataLayerEventListenerProps,
  DataLayerLoggingPermissions,
  EmptyObject,
} from '@snapchat/logging';
import { AbstractDataLayerEventListener } from '@snapchat/logging';

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

/**
 * Interface for the dataLayer events in Google Analytics 4.
 *
 * NOTE: This is purposely kept vague to make it easier to send custom events.
 */
export type GtagEvent =
  | ['js', Date, Record<string, unknown>?]
  // See https://developers.google.com/tag-platform/gtagjs/reference#get
  | ['get', string, string, (value: string) => void]
  // See https://developers.google.com/tag-platform/gtagjs/reference#set
  | ['set', Record<string, unknown>?]
  // See https://developers.google.com/tag-platform/gtagjs/reference#config
  | ['config', string, Record<string, unknown>?]
  // See https://developers.google.com/tag-platform/gtagjs/reference#consent
  | ['consent', string, Record<string, unknown>?]
  // See https://developers.google.com/tag-platform/gtagjs/reference#event
  | ['event', string, Record<string, unknown>?];

interface GoogleAnalytics4ClientEventListenerProps<
  LoggingContext extends EmptyObject = EmptyObject,
  CustomEvents extends EmptyObject = EmptyObject
> extends Omit<DataLayerEventListenerProps<LoggingContext, CustomEvents, GtagEvent>, 'dataLayer'> {
  gaId: string;
  nonce?: string;
  /**
   * See
   * https://developers.google.com/analytics/devguides/collection/ga4/set-up-ecommerce#verify-data
   */
  debugMode?: boolean;
  /** See https://developers.google.com/analytics/devguides/collection/ga4/views?client_type=gtag */
  sendPageView?: boolean;
  /** See https://developers.google.com/analytics/devguides/collection/ga4/user-id?client_type=gtag */
  userId?: string;
  /** Clock (for testing). */
  clock?: () => number;
}

/**
 * Adds a callback to a GA4 event.
 *
 * Appends to the array if there aren't any param objects. If there is a param object, sets the
 * callback on it.
 */
export function addGa4EventCallback(event: GtagEvent, callback: () => void): boolean {
  switch (event[0]) {
    case 'get':
    // falls through
    case 'set':
      // No callback required.
      return false;
  }
  // API documentation: https://developers.google.com/tag-platform/gtagjs/reference/parameters

  // If the event already has an object param,
  // l.e. event = ['event', 'foo', { bar: 'buzz' }],
  // Then we need to append the callback params to it.
  if (!event[2]) event[2] = {};
  event[2].event_callback = callback;
  event[2].event_timeout = 2_000; /* ms */

  // TODO: Merge event_callback if there's one already specified in the event.

  return true;
}

/**
 * Note that even though gtag() just adds arguments to window.dataLayer, However in GA44, events
 * HAVE to be wrapped in an Arguments constructor. This is not a requirement in GTM, but seems to be
 * one for GA4.
 */
function gtag(..._args: unknown[]) {
  // biome-ignore lint/style/noArguments: Doesn't seem to work w/ rest args because the types expects instance of "Arguments".
  // biome-ignore lint/correctness/noUndeclaredVariables: Every function technically has "arguments" in scope.
  window.dataLayer!.push(arguments);
}

/**
 * Event listener that sends events to Google Analytics 4.
 *
 * Requires the caller to format their own events (sadly) since GA can be configured with any
 * object.
 */
export class GoogleAnalytics4ClientEventListener<
  LoggingContext extends EmptyObject = EmptyObject,
  CustomEvents extends EmptyObject = EmptyObject,
  LoggingPermission extends DataLayerLoggingPermissions = DataLayerLoggingPermissions
> extends AbstractDataLayerEventListener<
  LoggingContext,
  CustomEvents,
  LoggingPermission,
  GtagEvent
> {
  private readonly gaProps: GoogleAnalytics4ClientEventListenerProps<LoggingContext, CustomEvents>;

  constructor(props: GoogleAnalytics4ClientEventListenerProps<LoggingContext, CustomEvents>) {
    super(props);
    this.gaProps = props;
  }

  /** @override addEventToDataLayer */
  protected addEventToDataLayer(event: unknown): void {
    if (!Array.isArray(event)) {
      throw new Error('Unexpected event for GA4. GA4 events are arrays.');
    }

    // TODO: The first argument in the array has to be either 'js' | 'config' | 'set' | 'get' | 'event'
    // So we can validate events here.

    gtag(...(event as unknown[]));
  }

  /** @override init */
  protected init(): Promise<void> {
    window.dataLayer = window.dataLayer ?? [];
    // Uses the GTAG Api
    const ga4Script = document.createElement('script');
    ga4Script.async = true;
    ga4Script.src = `https://www.googletagmanager.com/gtag/js?id=${this.gaProps.gaId}`;

    if (this.gaProps.nonce) ga4Script.nonce = this.gaProps.nonce;

    document.head.appendChild(ga4Script);

    // We need to configure datalayer with a custom record due to GA requirements
    // See https://developers.google.com/analytics/devguides/collection/ga4/events?client_type=gtag
    this.addEventToDataLayer([
      'js',
      this.gaProps.clock ? new Date(this.gaProps.clock()) : new Date(),
    ]);

    this.addEventToDataLayer([
      'config',
      this.gaProps.gaId,
      {
        debug_mode: this.gaProps.debugMode ?? false,
        send_page_view: this.gaProps.sendPageView ?? true,
        user_id: this.gaProps.userId,
      },
    ]);

    return Promise.resolve();
  }

  /** @override */
  protected addEventCallback = addGa4EventCallback;
}
